import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Check, AlertCircle } from 'lucide-react';
import { userService } from '../features/auth/services/userService';

const OTPVerification = ({ isOpen, onClose, onSuccess }) => {
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!otp) {
            setError('OTP is required');
            return;
        }

        if (otp.length !== 6) {
            setError('OTP must be 6 digits');
            return;
        }

        setLoading(true);

        try {
            const response = await userService.verifyPasswordChangeOTP(otp);

            console.log('OTP Verification Response:', response);

            if (response.message) {
                setSuccess(true);

                // Call success callback
                if (onSuccess) {
                    onSuccess();
                }

                // Close modal after 2 seconds
                setTimeout(() => {
                    handleClose();
                }, 2000);
            }
        } catch (err) {
            console.error('OTP verification error:', err);
            setError(err.response?.data?.message || 'Invalid OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setOtp('');
        setError(null);
        setSuccess(false);
        onClose();
    };

    const handleOtpChange = (e) => {
        const value = e.target.value.replace(/\D/g, ''); // Only allow digits
        if (value.length <= 6) {
            setOtp(value);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.75)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1001,
                    padding: '20px'
                }}
                onClick={handleClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        background: '#111827',
                        borderRadius: '12px',
                        padding: '24px',
                        maxWidth: '450px',
                        width: '100%',
                        border: '1px solid #1f2937',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)'
                    }}
                >
                    {/* Header */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '24px'
                    }}>
                        <h2 style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            color: '#fff',
                            margin: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}>
                            <Shield size={24} />
                            Verify OTP
                        </h2>
                        <button
                            onClick={handleClose}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#9ca3af',
                                cursor: 'pointer',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '4px',
                                transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = '#1f2937';
                                e.currentTarget.style.color = '#fff';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = '#9ca3af';
                            }}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Info Message */}
                    <div style={{
                        marginBottom: '20px',
                        padding: '12px',
                        background: '#0f1419',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#9ca3af',
                        lineHeight: '1.5'
                    }}>
                        An OTP has been sent to your registered email address. Please enter the 6-digit code to complete the password change.
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {/* OTP Input */}
                        <div style={{ marginBottom: '20px' }}>
                            <label style={{
                                display: 'block',
                                fontSize: '13px',
                                fontWeight: '500',
                                color: '#e5e7eb',
                                marginBottom: '8px'
                            }}>
                                Enter OTP
                            </label>
                            <input
                                type="text"
                                value={otp}
                                onChange={handleOtpChange}
                                disabled={loading || success}
                                maxLength={6}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    background: '#0f1419',
                                    border: '1px solid #374151',
                                    borderRadius: '6px',
                                    color: '#fff',
                                    fontSize: '18px',
                                    fontWeight: '600',
                                    letterSpacing: '8px',
                                    textAlign: 'center',
                                    outline: 'none',
                                    transition: 'border-color 0.2s',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                                onBlur={(e) => e.target.style.borderColor = '#374151'}
                                placeholder="000000"
                            />
                        </div>

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading || success}
                            style={{
                                width: '100%',
                                padding: '12px 20px',
                                background: success ? '#22c55e' : '#3b82f6',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: loading || success ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                transition: 'all 0.2s',
                                opacity: loading ? 0.6 : 1
                            }}
                            onMouseOver={(e) => {
                                if (!loading && !success) {
                                    e.currentTarget.style.background = '#2563eb';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (!loading && !success) {
                                    e.currentTarget.style.background = '#3b82f6';
                                }
                            }}
                        >
                            {loading ? (
                                <>
                                    <div style={{
                                        width: '18px',
                                        height: '18px',
                                        border: '2px solid #fff',
                                        borderTopColor: 'transparent',
                                        borderRadius: '50%',
                                        animation: 'spin 0.8s linear infinite'
                                    }} />
                                    Verifying...
                                </>
                            ) : success ? (
                                <>
                                    <Check size={18} />
                                    Password Changed Successfully!
                                </>
                            ) : (
                                <>
                                    <Shield size={18} />
                                    Verify OTP
                                </>
                            )}
                        </button>
                    </form>

                    {/* Error Message */}
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            style={{
                                marginTop: '16px',
                                padding: '12px',
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid #ef4444',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#ef4444',
                                fontSize: '13px'
                            }}
                        >
                            <AlertCircle size={16} />
                            {error}
                        </motion.div>
                    )}

                    {/* Success Message */}
                    {success && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            style={{
                                marginTop: '16px',
                                padding: '12px',
                                background: 'rgba(34, 197, 94, 0.1)',
                                border: '1px solid #22c55e',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#22c55e',
                                fontSize: '13px'
                            }}
                        >
                            <Check size={16} />
                            Your password has been changed successfully! You can now use your new password to login.
                        </motion.div>
                    )}
                </motion.div>

                {/* CSS Animation for spinner */}
                <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
            </motion.div>
        </AnimatePresence>
    );
};

export default OTPVerification;
